import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, ShieldX, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Separator } from "@akuna/ui";
import { sha256Hex } from "@/lib/utils/hash";
import { supabase } from "@/integrations/supabase/client";
import { formatDateBR, formatDateTimeBR } from "@/lib/date-utils";

type VerifyResult = "idle" | "checking" | "found" | "not_found" | "error";

interface PrescriptionInfo {
  prescription_id: string;
  type: string;
  issued_at: string | null;
  validity_in_days: number | null;
  vet_name: string | null;
  crmv: string | null;
  clinic_name: string | null;
  pet_name: string | null;
  pet_birth_date: string | null;
  signed_at: string | null;
  signed_pdf_sha256: string | null;
  is_signed: boolean;
  hash_valid?: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  standard: "Padrão",
  manipulated: "Manipulada",
  exam: "Requisição de Exames",
  controlled: "Controlada",
};

export default function VerifyPrescription() {
  const { t } = useTranslation("prescriptions");
  const [searchParams] = useSearchParams();
  const initialId = useMemo(() => searchParams.get("id") ?? "", [searchParams]);

  const [prescriptionId, setPrescriptionId] = useState(initialId);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<VerifyResult>("idle");
  const [info, setInfo] = useState<PrescriptionInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleVerify = async () => {
    setErrorMessage(null);
    setInfo(null);

    if (!prescriptionId.trim()) {
      setResult("error");
      setErrorMessage(t("verify.idRequired", { defaultValue: "Informe o ID da receita." }));
      return;
    }

    try {
      setResult("checking");

      let pdf_sha256: string | undefined;
      if (file) {
        const buffer = await file.arrayBuffer();
        pdf_sha256 = await sha256Hex(buffer);
      }

      const { data, error } = await supabase.functions.invoke("prescription-verify", {
        body: { prescription_id: prescriptionId.trim(), ...(pdf_sha256 ? { pdf_sha256 } : {}) },
      });

      if (error) throw new Error(error.message ?? t("verify.genericError", { defaultValue: "Erro ao verificar." }));

      const payload = data as { error?: string } & PrescriptionInfo;
      if (payload?.error === "prescription_not_found") {
        setResult("not_found");
        setErrorMessage(t("verify.notFound", { defaultValue: "Receita não encontrada." }));
        return;
      }
      if (payload?.error) {
        throw new Error(payload.error);
      }

      setInfo(payload);
      setResult("found");
    } catch (err) {
      console.error("Erro ao verificar receita", err);
      setResult("error");
      setErrorMessage(t("verify.genericError", { defaultValue: "Erro ao verificar a receita. Tente novamente." }));
    }
  };


  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              {t("verify.title", { defaultValue: "Consultar Receita Veterinaria" })}
            </CardTitle>
            <CardDescription>
              {t("verify.description", { defaultValue: "Consulte os dados registrados e confira a integridade do PDF assinado." })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rx-id">{t("verify.idLabel", { defaultValue: "ID da receita" })}</Label>
              <Input
                id="rx-id"
                value={prescriptionId}
                onChange={(e) => setPrescriptionId(e.target.value)}
                placeholder={t("verify.idPlaceholder", { defaultValue: "Cole o ID da receita aqui" })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rx-file">
                {t("verify.fileLabel", { defaultValue: "PDF assinado (opcional - para conferir integridade no sistema)" })}
              </Label>
              <Input
                id="rx-file"
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>

            <Button
              onClick={handleVerify}
              disabled={result === "checking"}
              className="w-full"
            >
              {result === "checking" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("verify.checking", { defaultValue: "Verificando..." })}
                </>
              ) : (
                t("verify.button", { defaultValue: "Verificar receita" })
              )}
            </Button>

            <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">
                {t("verify.itiTitle", { defaultValue: "Validacao oficial no ITI" })}
              </p>
              <p>
                {t("verify.itiDescription", {
                  defaultValue:
                    "Para validar a assinatura ICP-Brasil, use o QR Code da receita no VALIDAR ou envie o PDF assinado em validar.iti.gov.br. Se o portal solicitar, informe exatamente o mesmo código impresso na receita.",
                })}
              </p>
            </div>

            {/* Error / not found */}
            {(result === "error" || result === "not_found") && errorMessage && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <ShieldX className="h-4 w-4 shrink-0" />
                {errorMessage}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prescription info card */}
        {result === "found" && info && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("verify.resultTitle", { defaultValue: "Dados registrados da receita" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {/* Signature status */}
              <div className="flex flex-wrap items-center gap-2">
                {info.is_signed ? (
                  <Badge variant="default" className="flex items-center gap-1 bg-green-600 text-white hover:bg-green-700">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {t("verify.signedBadge", { defaultValue: "Documento assinado no sistema" })}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    {t("verify.unsignedBadge", { defaultValue: "Sem PDF assinado registrado" })}
                  </Badge>
                )}
                <Badge variant="outline">{TYPE_LABELS[info.type] ?? info.type}</Badge>
              </div>

              {/* Hash integrity result */}
              {info.hash_valid === true && (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-md p-3">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  {t("verify.hashValid", { defaultValue: "O arquivo PDF corresponde ao documento original. Integridade confirmada." })}
                </div>
              )}
              {info.hash_valid === false && (
                <div className="flex items-center gap-2 text-destructive bg-destructive/10 rounded-md p-3">
                  <XCircle className="h-4 w-4 shrink-0" />
                  {t("verify.hashInvalid", { defaultValue: "O arquivo PDF NÃO corresponde ao documento registrado. Pode ter sido alterado." })}
                </div>
              )}

              <Separator />

              {/* Fields */}
              <div className="grid gap-3 sm:grid-cols-2">
                {info.vet_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.vetName", { defaultValue: "Médico veterinário" })}</p>
                    <p className="font-medium">{info.vet_name}</p>
                  </div>
                )}
                {info.crmv && (
                  <div>
                    <p className="text-xs text-muted-foreground">CRMV</p>
                    <p className="font-medium">{info.crmv}</p>
                  </div>
                )}
                {info.clinic_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.clinic", { defaultValue: "Clínica" })}</p>
                    <p className="font-medium">{info.clinic_name}</p>
                  </div>
                )}
                {info.pet_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.petName", { defaultValue: "Paciente" })}</p>
                    <p className="font-medium">{info.pet_name}</p>
                  </div>
                )}
                {info.pet_birth_date && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.petBirthDate", { defaultValue: "Nascimento do paciente" })}</p>
                    <p className="font-medium">{formatDateBR(info.pet_birth_date)}</p>
                  </div>
                )}
                {info.issued_at && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.issuedAt", { defaultValue: "Data de emissão" })}</p>
                    <p className="font-medium">{formatDateBR(info.issued_at)}</p>
                  </div>
                )}
                {info.validity_in_days && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.validity", { defaultValue: "Validade" })}</p>
                    <p className="font-medium">{info.validity_in_days} {t("print.days", { defaultValue: "dias" })}</p>
                  </div>
                )}
                {info.signed_at && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("verify.signedAt", { defaultValue: "Assinado em" })}</p>
                    <p className="font-medium">{formatDateTimeBR(info.signed_at)}</p>
                  </div>
                )}
              </div>

              {info.signed_pdf_sha256 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t("verify.pdfHash", { defaultValue: "Hash SHA-256 do PDF assinado" })}</p>
                    <p className="font-mono text-xs break-all">{info.signed_pdf_sha256}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
